import {
  FileTrackerConfigProps,
  FileTrackerDataProps,
  isFileLoadingProps,
} from './file-tracker-types';
import {
  UploadForm,
  FileTrackerMetrices,
  FileList,
  RecentFiles,
} from './components';
import { ClientSelector } from './components/ClientSelector';
import { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Heading,
  Button,
  Box,
  Tooltip,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaRedo, FaExchangeAlt } from 'react-icons/fa/index.js';
import { getRandomNumbers } from '@ssar-app/uikit';

interface fileTrackerProps {
  fileTrackerConfig?: FileTrackerConfigProps;
  fileListAtTop?: boolean;
  fileTrackerDataCalls?: FileTrackerDataProps;
}

export const FileTracker = (props: fileTrackerProps) => {
  const {
    fileTrackerConfig = {},
    fileListAtTop = true,
    fileTrackerDataCalls,
  } = props;

  const {
    isFileListRequired = true,
    isRecentFilesRequired = true,
    isMetricesRequired = {
      fileStats: true,
      fileUploadTrend: true,
    },
    isQuickViewRequired = true,
    isUploadRequired = true,
    availbleSubCodes = '',
  } = fileTrackerConfig;

  const [key, setKey] = useState(getRandomNumbers(1)[0]);
  const [subCode, setSubscriberCode] = useState<string>(
    fileTrackerConfig.subCode || '',
  );
  const [showGetClient, setShowGetClient] = useState<boolean>(
    fileTrackerConfig.subCode === '' ? true : false,
  );
  const [isFileTrackerLoading, setIsFileTrackerLoading] =
    useState<isFileLoadingProps>({
      fileList: true,
      recentFiles: true,
    });

  const [isSwitchClient, setSwitchClient] = useState(false);

  useEffect(() => {
    isSwitchClient && setShowGetClient(true);
  }, [isSwitchClient]);

  useEffect(() => {
    !showGetClient && setSwitchClient(false);
  }, [showGetClient]);

  const getMetricesRequiredData = (isMetricesRequired: {
    fileStats: boolean;
    fileUploadTrend: boolean;
  }) => isMetricesRequired.fileStats || isMetricesRequired.fileUploadTrend;

  return (
    <Flex direction='column' key={key}>
      <Flex direction='row' justifyContent='space-between'>
        <Flex direction='column'>
          <Text fontSize='2xl' fontWeight='semibold' color={'black'}>
            Dashboard
          </Text>
          <Text size='xs' mb={2}>
            Welcome to File Tracker Dashboard
          </Text>
        </Flex>
        {!showGetClient && (
          <Flex direction='row' justifyContent='space-between' gap={4}>
            <Flex direction='column'>
              <Text size='xs'>Current Client</Text>
              <Heading size='sm' fontWeight='semibold'>
                {subCode}
              </Heading>
            </Flex>
            {(availbleSubCodes === '*' || availbleSubCodes.length > 1) && (
              <Tooltip label='Switch Client'>
                <Button
                  variant='outline'
                  size={'md'}
                  colorScheme={'gray'}
                  boxShadow={'md'}
                  sx={{
                    _hover: {
                      bg: 'white',
                      color: 'blue.800',
                      borderColor: 'blue.800',
                    },
                  }}
                  onClick={() => {
                    setSwitchClient(true);
                  }}
                  isDisabled={
                    isFileTrackerLoading.fileList ||
                    (isRecentFilesRequired
                      ? isFileTrackerLoading.recentFiles
                      : isRecentFilesRequired)
                  }
                >
                  <Icon alignContent={'center'}>
                    <Icon as={FaExchangeAlt} fontSize='xl' />
                  </Icon>
                </Button>
              </Tooltip>
            )}
          </Flex>
        )}
      </Flex>
      <Divider mb={2} mt={2} color='gray.400' />
      {showGetClient && (
        <ClientSelector
          setSubscriberCode={setSubscriberCode}
          fileTrackerDataCalls={fileTrackerDataCalls}
          setShowGetClient={setShowGetClient}
          isSwitchClient={isSwitchClient}
          subCodeList={availbleSubCodes}
        />
      )}

      <Flex direction={'column'} display={!showGetClient ? 'flex' : 'none'}>
        {!fileListAtTop && getMetricesRequiredData(isMetricesRequired) && (
          <FileTrackerMetrices
            isMetricesRequired={isMetricesRequired}
            getMetrics={fileTrackerDataCalls?.getMetrics}
          />
        )}
        {isFileListRequired && (
          <Flex
            direction='column'
            bg='white'
            borderRadius='md'
            shadow='md'
            mt='3'
            p='5'
          >
            <Flex justifyContent='space-between'>
              <Text
                fontSize='md'
                fontWeight='semibold'
                textTransform='uppercase'
                pb='4'
              >
                FILE TRACKER
              </Text>
              <Box>
                {isUploadRequired && (
                  <UploadForm
                    getUploadStatus={fileTrackerDataCalls?.getUploadStatus}
                  />
                )}
                <Button
                  mx={2}
                  colorScheme={'gray'}
                  variant='outline'
                  size={'sm'}
                  sx={{
                    _hover: {
                      bg: 'white',
                      color: 'blue.800',
                      borderColor: 'blue.800',
                    },
                  }}
                  onClick={() => setKey(getRandomNumbers(1)[0])}
                  leftIcon={<Icon as={FaRedo} />}
                  isDisabled={
                    isFileTrackerLoading.fileList ||
                    (isFileTrackerLoading.recentFiles && isRecentFilesRequired)
                  }
                >
                  {'Refresh'}
                </Button>
              </Box>
            </Flex>
            <FileList
              isQuickViewRequired={isQuickViewRequired}
              getFileStatus={fileTrackerDataCalls?.getFileStatus}
              getFileList={fileTrackerDataCalls?.getFileList}
              setIsFileTrackerLoading={setIsFileTrackerLoading}
              isFileTrackerLoading={isFileTrackerLoading}
              subCode={subCode}
            />
          </Flex>
        )}
        {isRecentFilesRequired && (
          <RecentFiles
            getRecentFiles={fileTrackerDataCalls?.getFileList}
            setIsFileTrackerLoading={setIsFileTrackerLoading}
            isFileTrackerLoading={isFileTrackerLoading}
            subCode={subCode}
          />
        )}
        {fileListAtTop && getMetricesRequiredData(isMetricesRequired) && (
          <FileTrackerMetrices
            isMetricesRequired={isMetricesRequired}
            getMetrics={fileTrackerDataCalls?.getMetrics}
          />
        )}
      </Flex>
    </Flex>
  );
};
